import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
} from "react";
import axios from "axios";
import styled from "styled-components";
import Chip from "@mui/material/Chip";
import DataTable from "react-data-table-component";
import useEntitesTable from "./useEntitesTable.js";
import Button from "components/CustomButtons/Button.js";
import CustomDeleteButton from "components/CustomDeleteButton/CustomDeleteButton.js";
import AddMaterialDialog from "./AddMaterialDialog.js";
import EditMaterialDialog from "./EditMaterialDialog";
import UserService from "services/UserService";
import AdvancedSearchContext from "context/AdvancedSearchContext";
import { makeStyles, withStyles } from "@mui/styles";
import Dialog from "@mui/material/Dialog";
import MuiDialogTitle from "@mui/material/DialogTitle";
import MuiDialogContent from "@mui/material/DialogContent";
import MuiDialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import CloseIcon from "@mui/icons-material/Close";
import { batiment } from "variables/patrimoine.js";
import { getSite, getFiles, downloadFile } from "services/ConfigServices";
import Edit from "@mui/icons-material/Edit";
import styles from "assets/jss/material-dashboard-pro-react/views/extendedTablesStyle.js";
import { materials, getObjectFromId } from "variables/lists/patrimoine.js";
import { NotificationManager } from 'react-notifications';


const useStyles = makeStyles(styles);

const styless = (theme) => ({
  root: {
    margin: 0,
    padding: '16px',
  },
  closeButton: {
    position: "absolute",
    right: '8px',
    top: '8px',
    color: '#9e9e9e',
  },
  button: {
    backgroundColor: "",
    bordertopleftradius: 0,
    borderbottomleftradius: 0,
    bordertoprightradius: "5px",
    borderbottomrightradius: "5px",
    height: "34px",
    width: "32px",
    textalign: "center",
    display: "flex",
    alignitems: "center",
    justifycontent: "center",
    "&:hover": {
      cursor: "pointer",
    },
  },
  demo2: {
    backgroundColor: "#fff",
    borderRadius: "5px",
  },
});

const DialogTitle = withStyles(styless)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: '16px',
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: '8px',
  },
}))(MuiDialogActions);

const Container = styled.div`
  border-bottom: 0.5px solid rgba(0, 0, 0, 0.15);
  border-top: 4px solid rgba(255, 255, 255, 1);
  background-color: white;
  margin-top: -0.5px;
`;

const ExpandableContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0.5rem 4rem 2rem;
`;
const ExpandableElement = styled.div`
  display: flex;
  flex-direction: row;
  margin-left: ${(props) => props.marginleft};
`;

const ElementTitle = styled.div`
  font-weight: 600;
  font-size: 12px;
  font-family: "Source Sans Pro",sans-serif;
`;

const ElementData = styled.div`
  margin-left: 4px;
  font-weight: 400;
  font-size: 12px;
  font-family: "Source Sans Pro",sans-serif;
`;

const ElementDataClickable = styled.div`
  margin-left: 4px;
  font-weight: 400;
  font-size: 12px;
  font-family: "Source Sans Pro",sans-serif;
  cursor: pointer;
  text-decoration: underline;
`;


const ExpandableComponent = props => {
  const { data } = props;
  return (
    <Container>
      <ExpandableContainer>
        {data.refmaterial ?
          <ExpandableElement>
            <ElementTitle>{batiment.structure.walls.materials.refmaterial} : </ElementTitle>
            <ElementData>{getObjectFromId(data.refmaterial, materials)?.name}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.uncertainty ?
          <ExpandableElement>
            <ElementTitle>{batiment.structure.walls.materials.uncertainty} : </ElementTitle>
            <ElementData>{data.uncertainty}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.thickness ?
          <ExpandableElement>
            <ElementTitle>{batiment.structure.walls.materials.thickness} : </ElementTitle>
            <ElementData>{data.thickness}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.thicknessprecision ?
          <ExpandableElement>
            <ElementTitle>{batiment.structure.walls.materials.thicknessprecision} : </ElementTitle>
            <ElementData>{data.thicknessprecision}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.comments ?
          <ExpandableElement>
            <ElementTitle>{batiment.structure.walls.materials.comments} : </ElementTitle>
            <ElementData>{data.comments}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.functionality ?
          <ExpandableElement>
            <ElementTitle>{batiment.structure.walls.materials.functionality} : </ElementTitle>
            <ElementData>{data.functionality}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.usualwall ?
          <ExpandableElement>
            <ElementTitle>{batiment.structure.walls.materials.usualwall} : </ElementTitle>
            <ElementData>{data.usualwall}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.thermicalconductivity ?
          <ExpandableElement>
            <ElementTitle>{batiment.structure.walls.materials.thermicalconductivity} : </ElementTitle>
            <ElementData>{data.thermicalconductivity}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.thermicalresistance ?
          <ExpandableElement>
            <ElementTitle>{batiment.structure.walls.materials.thermicalresistance} : </ElementTitle>
            <ElementData>{data.thermicalresistance}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.thermicalcapacity ?
          <ExpandableElement>
            <ElementTitle>{batiment.structure.walls.materials.thermicalcapacity} : </ElementTitle>
            <ElementData>{data.thermicalcapacity}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.volumicmass ?
          <ExpandableElement>
            <ElementTitle>{batiment.structure.walls.materials.volumicmass} : </ElementTitle>
            <ElementData>{data.volumicmass}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.carbonfootprint ?
          <ExpandableElement>
            <ElementTitle>{batiment.structure.walls.materials.carbonfootprint} : </ElementTitle>
            <ElementData>{data.carbonfootprint}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.greyenergy ?
          <ExpandableElement>
            <ElementTitle>{batiment.structure.walls.materials.greyenergy} : </ElementTitle>
            <ElementData>{data.greyenergy}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.vapordiffusion ?
          <ExpandableElement>
            <ElementTitle>{batiment.structure.walls.materials.vapordiffusion} : </ElementTitle>
            <ElementData>{data.vapordiffusion}</ElementData>
          </ExpandableElement> : <></>
        }
      </ExpandableContainer>
    </Container>
  );
};


const Materials = (props) => {
  const permis = JSON.parse(localStorage.getItem('permission')) || [];
  const realestateupdate = permis.includes("realestate_update");
  const { parameters } = props;
  const {
    gridState,
    error,
    refetch,
    invalidate,
    handleClose,
  } = useEntitesTable(
    parameters,
    <AddMaterialDialog
      parameters={parameters}
      closeModal={() => handleClose()}
      refetch={() => refetch()}
      deleteCounter={() => handleDeleteCounterChange()}
    />,
    "Nouveau matériau",
    "Ajout d'un matériau",
    "Liste matériaux"
  );
  const [deleteCounter, setDeleteCounter] = useState(0);
  const [open, setOpen] = useState(false);
  const [prevMaterialList, setPrevMaterialList] = useState();
  const appContext = useContext(AdvancedSearchContext);
  const { chartIdGroup } = appContext;

  const [clef, setClef] = useState("");
  const [refmaterial, setRefmaterial] = useState("");
  const [name, setName] = useState("");
  const [uncertainty, setUncertainty] = useState("");
  const [umaterial, setUmaterial] = useState("");
  const [thickness, setThickness] = useState("");
  const [thicknessprecision, setThicknessprecision] = useState("");
  const [comments, setComments] = useState("");
  const [category, setCategory] = useState("");
  const [walltype, setWalltype] = useState("");
  const [functionality, setFunctionality] = useState("");
  const [usualwall, setUsualwall] = useState("");
  const [thermicalconductivity, setThermicalconductivity] = useState("");
  const [thermicalresistance, setThermicalresistance] = useState("");
  const [thermicalcapacity, setThermicalcapacity] = useState("");
  const [volumicmass, setVolumicmass] = useState("");
  const [carbonfootprint, setCarbonfootprint] = useState("");
  const [greyenergy, setGreyenergy] = useState("");
  const [vapordiffusion, setVapordiffusion] = useState("");

  const handleDeleteCounterChange = (e) => {
    setDeleteCounter((prevCounter) => prevCounter + 1);
  };

  const handleClefChange = (event) => { setClef(event.target.value); };
  const handleNameChange = (event) => { setName(event.target.value); };
  const handleUncertaintyChange = (event) => { setUncertainty(event.target.value); };
  const handleUmaterialChange = (event) => {
    let uwall = event.target.value;
    let rtherm = 0;
    if (uwall > 0) {
      rtherm = Math.round((1 / uwall) * 100) / 100;
    }
    setUmaterial(uwall);
    setThermicalresistance(rtherm);
  };
  const handleThicknessChange = (event) => { setThickness(event.target.value); };
  const handleThicknessprecisionChange = (event) => { setThicknessprecision(event.target.value); };
  const handleCommentsChange = (event) => { setComments(event.target.value); };
  const handleCategoryChange = (event) => { setCategory(event.target.value); };
  const handleWalltypeChange = (event) => { setWalltype(event.target.value); };
  const handleFunctionalityChange = (event) => { setFunctionality(event.target.value); };
  const handleUsualwallChange = (event) => { setUsualwall(event.target.value); };
  const handleThermicalconductivityChange = (event) => { setThermicalconductivity(event.target.value); };
  const handleThermicalresistanceChange = (event) => {
    let rtherm = event.target.value;
    let uwall = 0;
    if (rtherm > 0) {
      uwall = Math.round((1 / rtherm) * 100) / 100;
    }
    setThermicalresistance(rtherm);
    setUmaterial(uwall);
  };
  const handleThermicalcapacityChange = (event) => { setThermicalcapacity(event.target.value); };
  const handleVolumicmassChange = (event) => { setVolumicmass(event.target.value); };
  const handleCarbonfootprintChange = (event) => { setCarbonfootprint(event.target.value); };
  const handleGreyenergyChange = (event) => { setGreyenergy(event.target.value); };
  const handleVapordiffusionChange = (event) => { setVapordiffusion(event.target.value); };
  const handleRefmaterialChange = (event) => {
    let mater = getObjectFromId(event.target.value, materials);
    if (mater && mater.clef && mater.clef !== '')
      setClef(mater.clef);
    else
      setClef("");
    if (mater && mater.name && mater.name !== '')
      setName(mater.name);
    else
      setName("");
    if (mater && mater.category && mater.category !== '')
      setCategory(mater.category);
    else
      setCategory("");
    if (mater && mater.walltype && mater.walltype !== '')
      setWalltype(mater.walltype);
    else
      setWalltype("");
    if (mater && mater.functionality && mater.functionality !== '')
      setFunctionality(mater.functionality);
    else
      setFunctionality("");
    if (mater && mater.usualwall && mater.usualwall !== '')
      setUsualwall(mater.usualwall);
    else
      setUsualwall("");
    if (mater && mater.thickness && mater.thickness !== '')
      setThickness(mater.thickness);
    else
      setThickness("");
    if (mater && mater.thermicalconductivity && mater.thermicalconductivity !== '')
      setThermicalconductivity(mater.thermicalconductivity);
    else
      setThermicalconductivity("");
    if (mater && mater.umaterial && mater.umaterial !== '')
      setUmaterial(mater.umaterial);
    else
      setUmaterial("");
    if (mater && mater.thermicalresistance && mater.thermicalresistance !== '')
      setThermicalresistance(mater.thermicalresistance);
    else
      setThermicalresistance("");
    if (mater && mater.thermicalcapacity && mater.thermicalcapacity !== '')
      setThermicalcapacity(mater.thermicalcapacity);
    else
      setThermicalcapacity("");
    if (mater && mater.volumicmass && mater.volumicmass !== '')
      setVolumicmass(mater.volumicmass);
    else
      setVolumicmass("");
    if (mater && mater.carbonfootprint && mater.carbonfootprint !== '')
      setCarbonfootprint(mater.carbonfootprint);
    else
      setCarbonfootprint("");
    if (mater && mater.greyenergy && mater.greyenergy !== '')
      setGreyenergy(mater.greyenergy);
    else
      setGreyenergy("");
    if (mater && mater.vapordiffusion && mater.vapordiffusion !== '')
      setVapordiffusion(mater.vapordiffusion);
    else
      setVapordiffusion("");
    setRefmaterial(event.target.value);
  };

  const updateState = useCallback((state) => console.log(state));

  const classes = useStyles();

  let dataMaterials = gridState.data;

  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleEditClose = () => {
    setOpen(false);
  };

  const columns = [
    {
      name: batiment.structure.walls.materials.name,
      selector: row => row.name,
      sortable: true,
    },
    {
      name: batiment.structure.walls.materials.key,
      selector: row => row.clef,
      sortable: true,
    },
    {
      name: batiment.structure.walls.materials.category,
      selector: row => row.category,
      sortable: true,
    },
    {
      name: batiment.structure.walls.materials.walltype,
      selector: row => row.walltype,
      sortable: true,
      wrap: true,
    },
    realestateupdate ?
      {
        name: "Actions",
        sortable: true,
        width: "8rem",
        // eslint-disable-next-line react/display-name
        cell: (row) => (
          <>
            <div className="actions-right">
              <Button
                color={"success"}
                className={classes.actionButton}
                onClick={editMaterial(row)}
              >
                <Edit className={classes.icon} />
              </Button>
              <CustomDeleteButton
                deleteTitle={"Suppression du matériau"}
                deleteSubTitle={
                  "Êtes-vous sûr de vouloir supprimer le matériau?"
                }
                handleSupprimer={deleteMaterial(row)}
              />
            </div>
          </>
        ),
      } : {},
  ];

  useEffect(() => {
    axios
      .get(
        getSite(chartIdGroup, "structure.walls.materials"),
        {
          headers: {
            Authorization: `Bearer ${UserService.getToken()}`,
          },
        }
      )
      .then((res) => {
        setPrevMaterialList(res.data.detail?.structure?.walls?.materials || []);
      });

  }, [deleteCounter]);


  const [idSelected, setIdSelected] = useState();

  const editMaterial = (row) => async () => {
    let obj = dataMaterials.find((o) => o.uuid === row.uuid);
    handleClickOpen();

    setIdSelected(obj.uuid);
    setClef(obj?.clef);
    setRefmaterial(obj?.refmaterial);
    setName(obj?.name);
    setUncertainty(obj?.uncertainty);
    setUmaterial(obj?.umaterial);
    setThickness(obj?.thickness);
    setThicknessprecision(obj?.thicknessprecision);
    setComments(obj?.comments);
    setCategory(obj?.category);
    setWalltype(obj?.walltype);
    setFunctionality(obj?.functionality);
    setUsualwall(obj?.usualwall);
    setThermicalconductivity(obj?.thermicalconductivity);
    setThermicalresistance(obj?.thermicalresistance);
    setThermicalcapacity(obj?.thermicalcapacity);
    setVolumicmass(obj?.volumicmass);
    setCarbonfootprint(obj?.carbonfootprint);
    setGreyenergy(obj?.greyenergy);
    setVapordiffusion(obj?.vapordiffusion);
  };

  const deleteMaterial = (row) => async () => {
    let obj = dataMaterials.find((o) => o.uuid === row.uuid);
    const indexToReplace = dataMaterials.findIndex((x) => x.uuid === obj.uuid);
    const materiallist = prevMaterialList;
    materiallist.splice(indexToReplace, 1);
    axios
      .put(
        getSite(chartIdGroup, "structure.walls.materials"),
        materiallist,
        {
          headers: {
            Authorization: `Bearer ${UserService.getToken()}`,
            'Content-Type': "application/json; charset=utf-8",
          },
        }
      )
      .then((res) => {
        if (res.status == 200) {
          NotificationManager.success("Matériau supprimé");
        }
        else {
          NotificationManager.error("Une erreur est survenue");
        }
        refetch();
      })
      .catch(() => {
        NotificationManager.error("Une erreur est survenue");
      });
  };

  return (
    <>
      <div
        style={{
          marginTop: "-1.8rem ",
          paddingTop: "0.5rem",
          //borderTop: "solid 1.2px rgba(0, 0, 0, 0.15)",
        }}
      >
        <DataTable
          paginationRowsPerPageOptions={[10,20,30,50,100]}
          columns={columns}
          persistTableHead
          pagination
          noHeader={true}
          subHeader
          onSelectedRowsChange={updateState}
          expandOnRowClicked
          expandableRows
          expandableRowDisabled={(row) => row.disabled}
          expandableRowsComponent={ExpandableComponent}
          progressPending={gridState.loading}
          progressComponent={<LinearProgress />}
          {...gridState}
        ></DataTable>
        <Dialog
          onClose={handleEditClose}
          aria-labelledby="edituser-dialog-title"
          fullWidth="true"
          maxWidth="sm"
          open={open}
        >
          <DialogTitle id="customized-dialog-title" onClose={handleEditClose}>
            Modifier le matériau
          </DialogTitle>
          <DialogContent dividers>
            <EditMaterialDialog
              parameters={parameters}
              closeModal={handleEditClose}
              refetch={refetch}
              materials={materials}
              idSelected={idSelected}
              prevMaterialList={prevMaterialList}
              clef={clef}
              refmaterial={refmaterial || ""}
              name={name}
              uncertainty={uncertainty}
              umaterial={umaterial}
              thickness={thickness}
              thicknessprecision={thicknessprecision}
              comments={comments}
              category={category}
              walltype={walltype}
              functionality={functionality}
              usualwall={usualwall}
              thermicalconductivity={thermicalconductivity}
              thermicalresistance={thermicalresistance}
              thermicalcapacity={thermicalcapacity}
              volumicmass={volumicmass}
              carbonfootprint={carbonfootprint}
              greyenergy={greyenergy}
              vapordiffusion={vapordiffusion}
              handleClefChange={handleClefChange}
              handleRefmaterialChange={handleRefmaterialChange}
              handleNameChange={handleNameChange}
              handleUncertaintyChange={handleUncertaintyChange}
              handleUmaterialChange={handleUmaterialChange}
              handleThicknessChange={handleThicknessChange}
              handleThicknessprecisionChange={handleThicknessprecisionChange}
              handleCommentsChange={handleCommentsChange}
              handleCategoryChange={handleCategoryChange}
              handleWalltypeChange={handleWalltypeChange}
              handleFunctionalityChange={handleFunctionalityChange}
              handleUsualwallChange={handleUsualwallChange}
              handleThermicalconductivityChange={handleThermicalconductivityChange}
              handleThermicalresistanceChange={handleThermicalresistanceChange}
              handleThermicalcapacityChange={handleThermicalcapacityChange}
              handleVolumicmassChange={handleVolumicmassChange}
              handleCarbonfootprintChange={handleCarbonfootprintChange}
              handleGreyenergyChange={handleGreyenergyChange}
              handleVapordiffusionChange={handleVapordiffusionChange}
            />
          </DialogContent>
          <DialogActions></DialogActions>
        </Dialog>
      </div>
    </>
  );
};

export default Materials;
